// INSIDO (11-shorts): helpers shared by the shorts row, player and recorder —
// autumn URLs for short videos, card label/gradient, recorder capabilities
// and the upload size limit. New file, not upstream.
import type { Client } from "stoat.js";

import type { FeedPost } from "../../../../organization/feed";

/** Recording cap in seconds */
export const MAX_RECORDING_SECONDS = 60;

/**
 * Target video bitrate. 60 s at 2 Mbps + 64 kbps audio is ~15.5 MB, under the
 * dev stack's 20 MB `attachments` limit.
 */
export const VIDEO_BITS_PER_SECOND = 2_000_000;

/** Target audio bitrate */
export const AUDIO_BITS_PER_SECOND = 64_000;

/** Maximum caption length */
export const MAX_CAPTION_LENGTH = 120;

/** Fallback upload limit when the API configuration does not expose one */
const DEFAULT_UPLOAD_LIMIT = 20_000_000;

/** Card background gradients (brand palette), picked deterministically per post */
const GRADIENTS = [
  "linear-gradient(160deg, #4f8cff, #1f3f8f)",
  "linear-gradient(160deg, #f5a524, #a3540f)",
  "linear-gradient(160deg, #2fb36b, #145c38)",
  "linear-gradient(160deg, #ef5a4c, #8a2219)",
  "linear-gradient(160deg, #9b6bff, #4a2a99)",
];

/**
 * Deterministic background gradient for a short (shown until/unless the
 * poster frame loads)
 * @param id Post id
 */
export function shortGradient(id: string): string {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

/**
 * Card label: first non-empty line of the body
 * @param post Short post
 */
export function shortLabel(post: FeedPost): string {
  return (
    post.content
      .split("\n")
      .map((line) => line.trim())
      .find(Boolean) ?? ""
  );
}

/**
 * Autumn URL of the short's video (first media id)
 * @param client Client
 * @param post Short post
 */
export function shortVideoURL(
  client: Client,
  post: FeedPost,
): string | undefined {
  const id = post.attachments[0];
  const autumn = client.configuration?.features.autumn.url;
  return id && autumn ? `${autumn}/attachments/${id}` : undefined;
}

/**
 * Whether in-app recording is possible in this browser
 */
export function canRecord(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

/**
 * Best supported recording MIME type: VP9+Opus, then VP8+Opus, then whatever
 * WebM/MP4 the browser supports; empty string lets the browser decide.
 */
export function pickRecorderMimeType(): string {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

/**
 * File extension for a recorded MIME type
 * @param mimeType Recorder MIME type
 */
export function extensionFor(mimeType: string): string {
  return mimeType.startsWith("video/mp4") ? "mp4" : "webm";
}

/**
 * Maximum size (bytes) autumn accepts for the `attachments` tag
 * @param client Client
 */
export function attachmentSizeLimit(client: Client): number {
  const limits = (
    client.configuration?.features as
      | {
          limits?: {
            global?: { file_upload_size_limits?: { attachments?: number } };
          };
        }
      | undefined
  )?.limits;
  return (
    limits?.global?.file_upload_size_limits?.attachments ?? DEFAULT_UPLOAD_LIMIT
  );
}
